import React from "react";
import { Navigate } from "react-router-dom";
import { Box, Container, Grid, CircularProgress, Typography } from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import { useAuth } from "../../../config/AuthContext";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import RelationshipFlowCard from "./inc/RelationshipFlowCard";
import { normalizeIrn } from "../../../utils/irn";

// This page is reachable by anyone granted the "Data" permission (standard
// ProtectedRoute/routeToPermission wiring, same as every other TpPortalMgt
// page), but it's meant to be admin-only regardless of how that permission
// happens to be granted — so it also hard-checks admin status here and
// bounces anyone who isn't, the same check used in TpUsersByType.jsx /
// TpInstructors.jsx for the "Admin" chip/tab.
const isAdminUser = (user) =>
  Boolean(user?.userTypes?.admin || user?.techPrepAdminFlag);

const Data = () => {
  const { currentUser } = useAuth();

  const { data: deliveryDetails, loading: deliveryDetailsLoading } =
    useFirestore("DeliveryDetails");
  const { data: programDelivery, loading: programDeliveryLoading } =
    useFirestore("ProgramDelivery");
  const { data: pathwayPrograms, loading: pathwayProgramsLoading } =
    useFirestore("PathwaysPrograms");
  const { data: partners, loading: partnersLoading } =
    useFirestore("TpPartners");
  const { data: courseShells, loading: courseShellsLoading } =
    useFirestore("TpCourseShell");
  const { data: users, loading: usersLoading } = useFirestore("users");
  const { data: students, loading: studentsLoading } =
    useFirestore("TpStudents");

  if (!currentUser) {
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdminUser(currentUser)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // ── Shared lookups ─────────────────────────────────────────────────────
  const findDeliveryDetailsForInstructor = (instructorId) =>
    (deliveryDetails || []).filter(
      (dd) =>
        String(dd.InstructorID) === String(instructorId) &&
        dd.Active !== false,
    );
  const findProgramDelivery = (programDeliveryId) =>
    (programDelivery || []).find(
      (pd) =>
        String(pd.ProgramDeliveryID).trim() ===
        String(programDeliveryId || "").trim(),
    );
  const findPathway = (pathwayId) =>
    (pathwayPrograms || []).find(
      (p) => String(p.PathwayID || p.id || "").trim() === String(pathwayId || "").trim(),
    );
  const findPartnerByName = (partnerName) =>
    (partners || []).find((p) => p.Tech_Prep_Partner === partnerName);
  // PartnerIRN is the stable id survives a Tech_Prep_Partner rename;
  // matching Course Shell relationships by name alone breaks the moment a
  // partner is renamed in TpPartners.
  const findPartnerByIRN = (partnerIRN) =>
    (partners || []).find(
      (p) => normalizeIrn(p.PartnerIRN) === normalizeIrn(partnerIRN),
    );
  const findUserById = (userId) =>
    (users || []).find((u) => u.id === userId);

  // ── 1. Instructor → Delivery → Partner → Site & Pathway ────────────────
  const instructorOptions = (users || [])
    .filter((u) => u.userTypes?.instructor === true)
    .map((u) => ({ value: u.id, label: u.name || "Unnamed Instructor" }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const getInstructorFlowSteps = (instructorId) => {
    const instructor = findUserById(instructorId);
    const steps = [
      { label: "Instructor", value: instructor?.name || instructorId },
    ];
    const details = findDeliveryDetailsForInstructor(instructorId);
    if (details.length === 0) {
      steps.push({ label: "Delivery Detail", value: "", empty: true });
      return steps;
    }
    details.forEach((dd, idx) => {
      const prefix = details.length > 1 ? `Delivery Detail ${idx + 1}` : "Delivery Detail";
      steps.push({
        label: `${prefix} — HEI Program`,
        value: dd.HEIProgramCode || "(none)",
      });
      const pd = findProgramDelivery(dd.ProgramDeliveryID);
      if (!pd) {
        steps.push({
          label: `${prefix} — Partner`,
          value: "",
          empty: true,
        });
        return;
      }
      steps.push({
        label: `${prefix} — Partner`,
        value: pd.SOD_NameForApp || pd.ProgramDeliveryID,
      });
      steps.push({
        label: `${prefix} — Site of Delivery`,
        value: pd.SiteOfDelivery || "(none)",
      });
      const pathway = findPathway(pd.PathwayID);
      steps.push({
        label: `${prefix} — Pathway`,
        value: pathway
          ? `${pathway.PathwayName}${pathway.PathwayAbbrev ? ` (${pathway.PathwayAbbrev})` : ""}`
          : "(none)",
        empty: !pathway,
      });
    });
    return steps;
  };

  // ── 2. Partner → Sites ──────────────────────────────────────────────────
  const partnerOptions = (partners || [])
    .map((p) => ({ value: p.id, label: p.Tech_Prep_Partner || "Unnamed Partner" }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const getPartnerSitesSteps = (partnerId) => {
    const partner = (partners || []).find((p) => p.id === partnerId);
    const steps = [
      { label: "Partner", value: partner?.Tech_Prep_Partner || partnerId },
    ];
    const sites = partner?.Sites || [];
    if (sites.length === 0) {
      steps.push({ label: "Site", value: "", empty: true });
      return steps;
    }
    sites.forEach((site, idx) => {
      steps.push({
        label: sites.length > 1 ? `Site ${idx + 1}` : "Site",
        value: site.SiteName || site.SiteID || "(unnamed site)",
      });
    });
    return steps;
  };

  // ── 3. Course Shell → Partner / Site / Course / Instructor / Pathway ──
  const courseShellOptions = (courseShells || [])
    .map((cs) => ({ value: cs.id, label: cs.shell_name || "(unnamed shell)" }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const getCourseShellSteps = (shellId) => {
    const shell = (courseShells || []).find((cs) => cs.id === shellId);
    if (!shell) return [{ label: "Course Shell", value: "", empty: true }];

    const steps = [{ label: "Course Shell", value: shell.shell_name }];

    const partner = shell.tp_partner_irn
      ? findPartnerByIRN(shell.tp_partner_irn)
      : findPartnerByName(shell.tp_partner);
    steps.push({
      label: "Partner",
      value: shell.tp_partner
        ? `${shell.tp_partner}${partner?.PartnerIRN ? ` (IRN ${partner.PartnerIRN})` : ""}`
        : "(none)",
      empty: !shell.tp_partner,
    });
    steps.push({
      label: "Site of Delivery",
      value: shell.tp_site_of_delivery || "(none)",
      empty: !shell.tp_site_of_delivery,
    });
    steps.push({
      label: "Course / Subject",
      value: shell.course
        ? `${shell.course}${shell.subject ? ` — ${shell.subject}` : ""}`
        : "(none)",
      empty: !shell.course,
    });
    const instructor = findUserById(shell.primary_tp_inst_ID);
    steps.push({
      label: "Primary Instructor",
      value: instructor?.name || "(none)",
      empty: !instructor,
    });
    const pathwayManager = findUserById(shell.tp_pathway_manager_ID);
    steps.push({
      label: "Pathway Manager",
      value: pathwayManager?.name || "(none)",
      empty: !pathwayManager,
    });
    const pathwayProgram = (pathwayPrograms || []).find(
      (p) => p.id === shell.tp_pathway_program_ID,
    );
    steps.push({
      label: "Pathway Program",
      value: pathwayProgram?.PathwayName || "(none)",
      empty: !pathwayProgram,
    });

    return steps;
  };

  // ── 4. Student → Delivery → Partner → Pathway (saved-data trace) ──────
  const studentOptions = (students || [])
    .map((s) => ({
      value: s.id,
      label: [s.FirstName, s.LastName].filter(Boolean).join(" ") || "(unnamed student)",
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const getStudentTraceSteps = (studentId) => {
    const student = (students || []).find((s) => s.id === studentId);
    if (!student) return [{ label: "Student", value: "", empty: true }];

    const steps = [
      {
        label: "Student",
        value: [student.FirstName, student.LastName].filter(Boolean).join(" "),
      },
    ];

    // DeliveryDetails records have never actually had a DeliveryDetailsID
    // field populated — the real unique identifier is the Firestore
    // document id, which is what a student's DeliveryDetailID is saved as.
    const dd = (deliveryDetails || []).find(
      (d) => String(d.id) === String(student.DeliveryDetailID),
    );
    steps.push({
      label: "Saved Delivery Detail (HEI Program)",
      value: dd?.HEIProgramCode || student.reach_contact_id || "(none)",
      empty: !dd && !student.reach_contact_id,
    });

    const pd = findProgramDelivery(student.ProgramDeliveryID);
    steps.push({
      label: "Saved Partner",
      value: pd?.SOD_NameForApp || student.ProgramDeliveryID || "(none)",
      empty: !student.ProgramDeliveryID,
    });

    steps.push({
      label: "Saved Pathway Abbreviation",
      value: student.Secondary_Delivery_ID || "(none)",
      empty: !student.Secondary_Delivery_ID,
    });

    // Cross-check: does the saved Partner's own PathwayID actually match the
    // saved Pathway Abbreviation? Surfaces stale/mismatched saved data.
    if (pd) {
      const pathway = findPathway(pd.PathwayID);
      const matches =
        pathway?.PathwayAbbrev &&
        student.Secondary_Delivery_ID &&
        pathway.PathwayAbbrev === student.Secondary_Delivery_ID;
      steps.push({
        label: "Partner's Current Pathway (cross-check)",
        value: pathway
          ? `${pathway.PathwayAbbrev || pathway.PathwayName}${matches ? " ✓ matches saved value" : " ⚠ differs from saved value"}`
          : "(none)",
        empty: !pathway,
      });
    }

    return steps;
  };

  // ── 5. Partner → Course Shells (reverse of #3) ─────────────────────────
  const getPartnerShellsSteps = (partnerId) => {
    const partner = (partners || []).find((p) => p.id === partnerId);
    const steps = [
      { label: "Partner", value: partner?.Tech_Prep_Partner || partnerId },
    ];
    const matchingShells = (courseShells || []).filter((cs) =>
      cs.tp_partner_irn
        ? normalizeIrn(cs.tp_partner_irn) === normalizeIrn(partner?.PartnerIRN)
        : cs.tp_partner === partner?.Tech_Prep_Partner,
    );
    if (matchingShells.length === 0) {
      steps.push({ label: "Course Shell", value: "", empty: true });
      return steps;
    }
    matchingShells.forEach((cs, idx) => {
      steps.push({
        label: matchingShells.length > 1 ? `Course Shell ${idx + 1}` : "Course Shell",
        value: cs.shell_name || "(unnamed shell)",
      });
    });
    return steps;
  };

  // ── 6. Pathway → Program Deliveries → Partners ─────────────────────────
  const pathwayOptions = (pathwayPrograms || [])
    .map((p) => ({ value: p.id, label: p.PathwayName || "Unnamed Pathway" }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const getPathwayFlowSteps = (pathwayId) => {
    const pathway = (pathwayPrograms || []).find((p) => p.id === pathwayId);
    const steps = [
      { label: "Pathway", value: pathway?.PathwayName || pathwayId },
    ];
    const matchingDeliveries = (programDelivery || []).filter(
      (pd) =>
        String(pd.PathwayID || "").trim() ===
        String(pathway?.PathwayID || pathway?.id || "").trim(),
    );
    if (matchingDeliveries.length === 0) {
      steps.push({ label: "Program Delivery / Partner", value: "", empty: true });
      return steps;
    }
    matchingDeliveries.forEach((pd, idx) => {
      const prefix =
        matchingDeliveries.length > 1 ? `Program Delivery ${idx + 1}` : "Program Delivery";
      steps.push({
        label: `${prefix} — Partner`,
        value: pd.SOD_NameForApp || pd.ProgramDeliveryID,
      });
      steps.push({
        label: `${prefix} — Site`,
        value: pd.SiteOfDelivery || "(none)",
      });
    });
    return steps;
  };

  const relationships = [
    {
      title: "Instructor → Delivery → Partner → Site & Pathway",
      selectLabel: "Instructor",
      options: instructorOptions,
      optionsLoading: usersLoading || deliveryDetailsLoading,
      getSteps: getInstructorFlowSteps,
    },
    {
      title: "Partner → Sites",
      selectLabel: "Partner",
      options: partnerOptions,
      optionsLoading: partnersLoading,
      getSteps: getPartnerSitesSteps,
    },
    {
      title: "Course Shell → Partner / Site / Course / Instructor / Pathway",
      selectLabel: "Course Shell",
      options: courseShellOptions,
      optionsLoading: courseShellsLoading,
      getSteps: getCourseShellSteps,
    },
    {
      title: "Student → Delivery → Partner → Pathway (saved-data trace)",
      selectLabel: "Student",
      options: studentOptions,
      optionsLoading: studentsLoading,
      getSteps: getStudentTraceSteps,
    },
    {
      title: "Partner → Course Shells",
      selectLabel: "Partner",
      options: partnerOptions,
      optionsLoading: partnersLoading || courseShellsLoading,
      getSteps: getPartnerShellsSteps,
    },
    {
      title: "Pathway → Program Deliveries → Partners",
      selectLabel: "Pathway",
      options: pathwayOptions,
      optionsLoading: pathwayProgramsLoading,
      getSteps: getPathwayFlowSteps,
    },
  ];

  const anyLoading =
    deliveryDetailsLoading ||
    programDeliveryLoading ||
    pathwayProgramsLoading ||
    partnersLoading ||
    courseShellsLoading ||
    usersLoading ||
    studentsLoading;

  return (
    <Box sx={{ paddingTop: "80px" }}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
        <TechPrepPortalMgtBreadCrumbs pageName="Data" />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Pick a record in any card below to see how it actually flows
          through the related collections, live.
        </Typography>

        {anyLoading ? (
          <Box display="flex" justifyContent="center" mt={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {relationships.map((rel) => (
              <Grid item xs={12} md={4} key={rel.title}>
                <RelationshipFlowCard {...rel} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default Data;
